import { gql } from '@apollo/client/core';
import { client } from './apolloClient';
import { joinErrors } from './utils';
import {
  authRefusalMessage,
  defaultCallbackUrl,
  hostedLoginRedirect,
  withRedirectUri,
} from './hostedLoginClient';

/** A refusal worth showing as-is, rather than the generic "Login failed". */
export class UserFacingAuthError extends Error {}

/** True when the browser is leaving for a hosted SSO/MFA page; throws for a readable refusal. */
function followHostedLogin(errors: unknown): boolean {
  const hosted = hostedLoginRedirect(errors, defaultCallbackUrl(window));
  if (hosted && hosted.url) {
    window.location.assign(hosted.url);
    return true;
  }
  const message = (hosted && hosted.message) || authRefusalMessage(errors);
  if (message) throw new UserFacingAuthError(message);
  return false;
}

/**
 * Resolves to the session token, or to null when the sign-in continues on
 * api-v2's hosted SSO/MFA pages (the browser is already navigating away).
 */
export async function authenticateUser(email: string, password: string): Promise<string | null> {
  let result;
  try {
    result = await client.mutate<{
      loginWithPassword: { loginToken: string };
    }>({
      mutation: gql`
        mutation login($email: String!, $password: String!) {
          loginWithPassword(email: $email, password: $password) {
            loginToken
          }
        }
      `,
      variables: { email, password },
    });
  } catch (err) {
    if (followHostedLogin(err)) return null;
    throw err;
  }

  const token = result?.data?.loginWithPassword?.loginToken;

  if (!result.data && !result.errors)
    throw new Error(
      'No errors returned by @apollo/client despite they have been returned by Server'
    );

  if (result.errors && result.errors.length && followHostedLogin(result.errors)) return null;

  const err = joinErrors(result?.errors);

  if (err) {
    throw new Error(`Request errored: ${err}`);
  }

  if (!token) throw new Error('No token returned');

  return token;
}

export interface SsoLoginOptions {
  ssoAvailable: boolean;
  ssoRequired: boolean;
  providerName: string | null;
  providerKind: string | null;
  startUrl: string | null;
}

export async function fetchSsoLoginOptions(email: string): Promise<SsoLoginOptions | null> {
  try {
    const result = await client.query<{ ssoLoginOptions: SsoLoginOptions }>({
      query: gql`
        query ssoLoginOptions($email: String!) {
          ssoLoginOptions(email: $email) {
            ssoAvailable
            ssoRequired
            providerName
            providerKind
            startUrl
          }
        }
      `,
      variables: { email },
    });
    return result?.data?.ssoLoginOptions ?? null;
  } catch (err) {
    return null;
  }
}

export function ssoStartUrl(startUrl: string | null): string | null {
  return withRedirectUri(startUrl, defaultCallbackUrl(window));
}

/** Exchanges the hosted pages' one-time code for a session, plus the email the CMS displays. */
export async function completeHostedLogin(
  handoffCode: string
): Promise<{ authToken: string; userEmail: string }> {
  const result = await client.mutate<{ completeHostedLogin: { loginToken: string } }>({
    mutation: gql`
      mutation completeHostedLogin($handoffCode: String!) {
        completeHostedLogin(handoffCode: $handoffCode) {
          loginToken
        }
      }
    `,
    variables: { handoffCode },
  });
  const authToken = result?.data?.completeHostedLogin?.loginToken;
  if (!authToken) {
    throw new UserFacingAuthError('This sign-in link has expired or was already used. Start again.');
  }

  // The store has no token yet, so the auth middleware would send "Bearer null".
  const me = await client.query<{ me: { userEmail: string | null } }>({
    query: gql`
      query hostedLoginMe {
        me {
          userEmail
        }
      }
    `,
    context: { headers: { authorization: `Bearer ${authToken}` } },
  });
  return { authToken, userEmail: me?.data?.me?.userEmail || '' };
}
